import { t } from "ttag";
import { z } from "zod";

import { FIELD_TYPE } from "@/shared/models/legacy-constants";
import { dateOfBirthSchema } from "@/shared/models/legacy-schemas";
import {
  AVAILABLE_LANGUAGE,
  GENDER,
  MAX_HEIGHT_CENTIMETERS,
  MAX_HEIGHT_FEET,
  MAX_HEIGHT_INCHES,
  MAX_WEIGHT_KG,
  MAX_WEIGHT_LBS,
  REFILL_TYPE,
  UNIT_SYSTEM,
} from "./constants";

export const availableLanguageSchema = z.enum(AVAILABLE_LANGUAGE, {
  error: (issue) =>
    issue.input === undefined
      ? t`Please select your preferred language`
      : t`Invalid language type`,
});

export const paginationParamsSchema = z.object({
  page: z.number(),
  pageSize: z.number(),
});

export const userAttributesSchema = z.object({
  state: z.string({ error: () => t`State is required` }),
  gender: z.enum(GENDER, {
    error: () => t`Sex assigned at birth is required`,
  }),
  dateOfBirth: dateOfBirthSchema,
});

const numberError: z.ZodErrorMap = (issue) => {
  if (issue.code === "invalid_type" && isNaN(Number(issue.received))) {
    return t`This field is required`;
  }
};

const heightSchema = z
  .number({ error: numberError })
  .int({ error: () => t`Please enter a whole number` })
  .nonnegative({ error: () => t`Must be a positive value` });

export const weightSchema = z
  .number({ error: numberError })
  .int({ error: () => t`Please enter a whole number` })
  .positive({ error: () => t`Must be a positive value` });

export const imperialBmiInputsSchema = z.object({
  heightFeet: heightSchema.max(MAX_HEIGHT_FEET, {
    error: () => t`Must be ${MAX_HEIGHT_FEET} ft or less`,
  }),
  heightInches: heightSchema.max(MAX_HEIGHT_INCHES, {
    error: () => t`Must be ${MAX_HEIGHT_INCHES} in or less`,
  }),
  weightInPounds: weightSchema.max(MAX_WEIGHT_LBS, {
    error: () => t`Must be ${MAX_WEIGHT_LBS} lbs or less`,
  }),
});

export const metricBmiInputsSchema = z.object({
  heightInCentimeters: heightSchema.max(MAX_HEIGHT_CENTIMETERS, {
    error: () => t`Must be ${MAX_HEIGHT_CENTIMETERS} cm or less`,
  }),
  weightInKilograms: weightSchema.max(MAX_WEIGHT_KG, {
    error: () => t`Must be ${MAX_WEIGHT_KG} kg or less`,
  }),
});

export const bmiFormSchema = z.discriminatedUnion("unitSystem", [
  imperialBmiInputsSchema.extend({
    unitSystem: z.literal(UNIT_SYSTEM.IMPERIAL),
  }),
  metricBmiInputsSchema.extend({ unitSystem: z.literal(UNIT_SYSTEM.METRIC) }),
]);

export const optionSchema = z.object({ value: z.string(), label: z.string() });

export const answerSchema = z.object({
  id: z.number(),
  answer: z.string(),
  answerGroupId: z.number(),
  nextAnswerGroupId: z.number().nullable(),
  disqualifier: z.boolean(),
  unique: z.boolean().optional(),
});

export const questionSchema = z.object({
  id: z.number(),
  title: z.string(),
  subtitle: z.string().nullish(),
  type: z.enum([FIELD_TYPE.CHECKBOX, FIELD_TYPE.RADIO]),
  onboardingAnswers: z.array(answerSchema),
});

export const refillTypeSchema = z.enum(REFILL_TYPE);
